import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, Outlet } from "react-router-dom";
import {
    activeTabHandler,
    checkSubModulesListPermissions,
    getActiveModule,
    titleCase,
} from "@ts/utils";
import TabsErrorComponent from "@components/error-boundaries/TabsErrorComponent";
import { useGetModulesData } from "@hooks/AllCustomHooks";
import { mainContext, tabLinksData } from "@ts/constants";
import {
    CAMPAIGN_ORIGIN,
    ILinks,
    IMainContext,
    IShowDeltaValues,
    OptionValues,
    StateTypeValues,
    SubModule,
} from "@ts/common-types";
import $ from "jquery";
import { useDispatch, useSelector } from "react-redux";
import {
    updateAddToAdgroupModalData,
    updateAddToAdgroupModalDisplay,
} from "@/features/modalSlice";
import { Select, Switch } from "antd";
import { updateShowDeltaValues } from "@/features/gridDataSlice";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { Actions } from "@ts/common-types";
import { getClientId } from "@ts/utils";
import ExportGridBtn from "../buttons/ExportGridBtn";

type TTabsComponent = {
    onBtnExport: () => void;
};

const LinkContainer: React.FC<ILinks> = ({
    title,
    name,
    id,
    path,
    activeModule,
}) => {
    return (
        <>
            <TabsErrorComponent>
                <Link
                    id={id}
                    className={`react-tabs__tab react-tabs${name === SubModule.OVERVIEW || activeModule === name
                            ? "__tab--selected"
                            : "__disabled"
                        }`}
                    to={path}
                    key={id}
                    onClick={() => activeTabHandler(name)}
                >
                    <span>
                        {titleCase(title)}&nbsp;
                        {name !== SubModule.OVERVIEW && (
                            <>
                                <span id={`${name}-badge`}>
                                    <i className="spinner-grow spinner-grow-sm" />
                                </span>
                            </>
                        )}
                    </span>
                </Link>
            </TabsErrorComponent>
        </>
    );
};

const TabsComponent: React.FC<TTabsComponent> = React.memo(
    ({ onBtnExport }) => {
        const [campaignOrigin, setCampaignOrigin] = useState<string>(OptionValues.ALL);
        const [campaignStatusType, setCampaignStatusType] = useState<string>(OptionValues.ALL);
        const campaignOriginOptions = [{ label: "All Campaigns", value: OptionValues.ALL },
        { label: "1DS Campaigns", value: CAMPAIGN_ORIGIN.REVINITI },
        { label: "Blinkit Campaigns", value: CAMPAIGN_ORIGIN.EXTERNAL },
        ];
        const campaignStatusTypeOptions = [{ label: "All Statuses", value: OptionValues.ALL },
        { label: "Active", value: StateTypeValues.ENABLED },
        { label: "Disabled", value: StateTypeValues.ARCHIVED },
        ];
        const modulesList = useGetModulesData({ type: "sub-modules" });
        const activeModule =
            getActiveModule().activeModule || SubModule.OVERVIEW;
        const {
            manualRecommendationsGridApi,
            revinitiRecommendationsGridApi,
            autoRecommendationsGridApi,
            cerbosPermissions,
            campaignsGridApi,
        } = useContext<IMainContext>(mainContext);
        const tabsData = useMemo(() => {
            return tabLinksData.filter((data) =>
                modulesList?.includes(data.name)
            );
        }, [modulesList]);
        const showDeltaValues = useSelector(
            (state: IShowDeltaValues) => state.showDeltaValues.value
        );
        const createCampaignFeature = useFeatureIsOn("CAMPAIGN_WIZARD_FEATURE") || false;
        const activeClientId: number = getClientId();
        const actions = useGetModulesData({ type: "actions" });

        const isCampaignCreationVisible = useMemo(() => {
            return (
                createCampaignFeature &&
                actions.includes(Actions.BULK_CAMPAIGN_CREATION)
            );
        }, [createCampaignFeature, activeClientId, actions]);
        const dispatch = useDispatch();

        useEffect(() => {
            $("#add-to-adgroup-button").css({
                display: "none",
            });
        }, [activeModule]);

        const onChange = (checked: boolean) => {
            dispatch(updateShowDeltaValues(checked));
        };

        const onCampaignTypeChange = (value: string) => {
            setCampaignOrigin(value);
            const filterModel = campaignsGridApi.getFilterModel();

            if (value === OptionValues.ALL) {
                const { origin, ...restFilters } = filterModel;
                campaignsGridApi.setFilterModel(restFilters);
            } else {
                campaignsGridApi.setFilterModel({
                    ...filterModel,
                    origin: {
                        filterType: "set",
                        values: [value],
                    },
                });
            }
        };

        const onCampaignStatusTypeChange = (value: string) => {
            setCampaignStatusType(value);
            const filterModel = campaignsGridApi.getFilterModel();

            if (value === OptionValues.ALL) {
                const { state, automation, ...restFilters } = filterModel;
                campaignsGridApi.setFilterModel(restFilters);
            } else if (value === StateTypeValues.ENABLED) {
                campaignsGridApi.setFilterModel({
                    ...filterModel,
                    state: {
                        filterType: "set",
                        values: [StateTypeValues.ARCHIVED, StateTypeValues.ENABLED],
                    },
                    automation: {
                        filterType: "set",
                        values: ["true"],
                    },
                    campaign_status: {
                        filterType: "set",
                        values: ["active","on_hold","stopped"],
                    },
                });
            } else if (value === StateTypeValues.ARCHIVED) {
                campaignsGridApi.setFilterModel({
                    ...filterModel,
                    state: {
                        filterType: "set",
                        values: [value],
                    },
                    automation: {
                        filterType: "set",
                        values: ["false",null],
                    },
                    campaign_status: {
                        filterType: "set",
                        values: ["completed","draft","stopped"],
                    },
                });
            }
        };

        const tabsPermissions = useMemo(() => {
            const permissions =
                checkSubModulesListPermissions(cerbosPermissions);
            return permissions;
        }, [cerbosPermissions]);

        const isKeywordRecommendationModule = useMemo(() => {
            return [
                SubModule.KEYWORD_MANUAL,
                SubModule.KEYWORD_REVINITI,
                SubModule.KEYWORD_AUTO,
            ].includes(activeModule as SubModule);
        }, [activeModule]);

        return (
            <>
                <div className="Tabs border-bottom" id="myTab">
                    <div className="tabs-exportbtn__container">
                        {tabsData.map(({ title, name, id, path }) => {
                            if (!tabsPermissions.includes(name)) {
                                return (
                                    <LinkContainer
                                        key={id}
                                        {...{
                                            title,
                                            name,
                                            id,
                                            path,
                                            activeModule,
                                        }}
                                    />
                                );
                            }
                        })}
                    </div>

                    <div
                        style={{
                            display: `${activeModule === SubModule.OVERVIEW
                                    ? "none"
                                    : "flex"
                                }`,
                        }}
                    >
                        <button
                            id="add-to-adgroup-button"
                            data-bs-name="Add To AdGroup"
                            className="btn btn-sm btn-primary ms-auto mx-3 my-1"
                            type="button"
                            style={{
                                display: "none",
                            }}
                            onClick={() => {
                                const api =
                                    activeModule === SubModule.KEYWORD_MANUAL
                                        ? manualRecommendationsGridApi
                                        : activeModule ===
                                            SubModule.KEYWORD_REVINITI
                                            ? revinitiRecommendationsGridApi
                                            : autoRecommendationsGridApi;
                                dispatch(updateAddToAdgroupModalData(api));
                                dispatch(updateAddToAdgroupModalDisplay(true));
                            }}
                        >
                            Add To AdGroup
                        </button>

                        {!isKeywordRecommendationModule && (
                            <div className="ms-auto mx-3 my-auto">
                                <strong
                                    className="me-2 d-inline"
                                    style={{
                                        color: "rgb(116,116,116)",
                                        fontSize: "12.5px",
                                    }}
                                >
                                    Show/Hide Delta Values
                                </strong>
                                <Switch
                                    title="Show Delta values"
                                    defaultChecked={showDeltaValues}
                                    onChange={onChange}
                                    size="small"
                                />
                            </div>
                        )}
                        <div className="ms-auto mx-3 my-auto">
                            <Select
                                value={campaignStatusType}
                                options={campaignStatusTypeOptions}
                                onChange={onCampaignStatusTypeChange}
                                style={{ width: '17ch' }}
                            />
                        </div>
                        {
                            isCampaignCreationVisible && (
                                <div className="ms-auto mx-3 my-auto">
                                    <Select
                                        value={campaignOrigin}
                                        options={campaignOriginOptions}
                                        onChange={onCampaignTypeChange}
                                        style={{ width: '17ch' }}
                                    />
                                </div>

                            )
                        }
                        <ExportGridBtn
                            onClickFunction={() => onBtnExport()}
                            isSubModule={true}
                        />
                    </div>
                </div>
                <Outlet />
            </>
        );
    }
);

export default TabsComponent;